// Browser (Web Notifications API) pop-ups for ticket activity.
//
// Two gates before anything is shown: the browser permission (asked once via
// a user gesture) and an app-level preference in localStorage. Notifications
// are suppressed while the tab is focused — the in-app UI already shows the
// change — except explicit test notifications.

const PREF_KEY = 'ctticket-browser-notify';

export function isNotifySupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function notifyPermission(): NotificationPermission {
  return isNotifySupported() ? Notification.permission : 'denied';
}

export function getNotifyPref(): boolean {
  return localStorage.getItem(PREF_KEY) === 'on';
}

export function setNotifyPref(on: boolean): void {
  localStorage.setItem(PREF_KEY, on ? 'on' : 'off');
}

/** Ask for browser permission (must be called from a user gesture). */
export async function requestNotifyPermission(): Promise<NotificationPermission> {
  if (!isNotifySupported()) return 'denied';
  if (Notification.permission !== 'default') return Notification.permission;
  try {
    return await Notification.requestPermission();
  } catch {
    return 'denied';
  }
}

export interface TicketNotification {
  title: string;
  body?: string;
  /** In-app path to open when the notification is clicked. */
  url?: string;
  /** Same tag replaces an earlier notification instead of stacking. */
  tag?: string;
  /** Show even when the tab is focused (used by the test button). */
  force?: boolean;
}

// ── Web Push (works with the tab / browser closed) ─────────────────────────

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(b64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

export function isPushSupported(): boolean {
  return isNotifySupported() && 'serviceWorker' in navigator && 'PushManager' in window;
}

async function swRegistration(): Promise<ServiceWorkerRegistration> {
  const reg = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;
  return reg;
}

/**
 * Register the service worker and subscribe this browser to Web Push,
 * then store the subscription server-side. Requires granted permission.
 * Returns true when the device is subscribed.
 */
export async function enablePushSubscription(
  getPublicKey: () => Promise<string | null>,
  saveSubscription: (sub: PushSubscriptionJSON) => Promise<void>,
): Promise<boolean> {
  if (!isPushSupported() || Notification.permission !== 'granted') return false;
  const key = await getPublicKey();
  if (!key) return false;

  const reg = await swRegistration();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key) as BufferSource,
    });
  }
  await saveSubscription(sub.toJSON());
  return true;
}

/** Unsubscribe this browser and tell the server to drop the subscription. */
export async function disablePushSubscription(
  removeSubscription: (endpoint: string) => Promise<void>,
): Promise<void> {
  if (!isPushSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration('/sw.js');
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await removeSubscription(sub.endpoint).catch(() => undefined);
    await sub.unsubscribe().catch(() => undefined);
  }
}

/** Whether this browser currently holds a push subscription. */
export async function hasPushSubscription(): Promise<boolean> {
  if (!isPushSupported()) return false;
  const reg = await navigator.serviceWorker.getRegistration('/sw.js');
  const sub = await reg?.pushManager.getSubscription();
  return !!sub;
}

export function showBrowserNotification(n: TicketNotification): boolean {
  if (!isNotifySupported() || Notification.permission !== 'granted') return false;
  if (!n.force && !getNotifyPref()) return false;
  // Tab visible and focused → the app itself is the notification.
  if (!n.force && document.visibilityState === 'visible' && document.hasFocus()) return false;

  try {
    const notification = new Notification(n.title, {
      body: n.body,
      tag: n.tag,
      icon: '/icons/icon-192.png',
    });
    notification.onclick = () => {
      window.focus();
      if (n.url) window.location.href = n.url;
      notification.close();
    };
    return true;
  } catch {
    // Some platforms (e.g. Android Chrome) only allow notifications via a
    // ServiceWorker registration — fail quietly.
    return false;
  }
}
